// Bottom sheets while the keyboard is up (iOS app, 1.5.2 — founder: Edit
// profile "jumps half an inch and distorts" when a field is tapped).
//
// Why it jumped: with Keyboard.resize = native the plugin shrinks the webview
// only AFTER the keyboard has finished rising (duration + 0.2s, see
// @capacitor/keyboard Keyboard.m). Until then the keyboard simply covers the
// bottom of the sheet; then the viewport snaps shorter and every sheet
// re-lays out against it in one frame — max-height 86% of a smaller screen,
// re-anchored to a new bottom edge. On closing, the webview grows back 10ms
// after the keyboard starts down, so the sheet snapped again.
//
// The fix: the moment `keyboardWillShow` says how tall the keyboard will be,
// every open sheet is laid out for the final screen — its overlay is exactly
// the space above the keyboard (the same number before and after the webview
// shrinks, so the shrink moves nothing), and the sheet's TOP STAYS WHERE IT
// WAS: only its bottom comes up to meet the keyboard. A sheet short enough to
// fit whole above the keyboard rides up with it instead; one that would be
// left too short to use grows upward, never past the tall sheet's top gap.
// Any movement of the top glides (FLIP on transform). Closing reverses it,
// once the webview has grown back (the plugin grows it ~10ms after the
// keyboard starts down), so nothing is ever laid out against a screen size
// the webview does not have.
//
// Plain DOM on purpose: BottomSheet stays a dumb component and every sheet
// in the app gets this without opting in.

/** The least a tall sheet keeps above the keyboard before it grows upward. */
const MIN_SHEET = 280;
const GLIDE = { duration: 380, easing: "cubic-bezier(0.32, 0.72, 0.33, 1)" };

const reducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const sheetOf = (overlay: HTMLElement) => overlay.querySelector<HTMLElement>(":scope > .bsheet");

// Moves nothing on screen by itself: measures the sheet's top before and
// after `change` and glides it from the old place to the new one.
function glide(sheet: HTMLElement, change: () => void) {
  const before = sheet.getBoundingClientRect().top;
  change();
  const delta = before - sheet.getBoundingClientRect().top;
  if (Math.abs(delta) < 1 || reducedMotion() || typeof sheet.animate !== "function") return;
  sheet.animate([{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }], GLIDE);
}

/** Lays every open sheet out for a keyboard that leaves `avail` px above it. */
export function fitSheetsAboveKeyboard(avail: number): void {
  const root = document.documentElement;
  root.style.setProperty("--kb-avail", `${Math.round(avail)}px`);
  document.querySelectorAll<HTMLElement>(".bsheet-overlay").forEach((overlay) => {
    const sheet = sheetOf(overlay);
    if (!sheet) return;
    // Where the sheet sat with the keyboard down — remembered across field
    // changes (each one fires keyboardWillShow again) so it is measured once.
    if (!sheet.dataset.kbTop) sheet.dataset.kbTop = String(sheet.getBoundingClientRect().top - overlay.getBoundingClientRect().top);
    const top = Number(sheet.dataset.kbTop);
    const body = sheet.querySelector<HTMLElement>(".bsheet-body");
    const scrolled = body?.scrollTop ?? 0;
    glide(sheet, () => {
      overlay.classList.add("bsheet-overlay--kb");
      // The height the sheet's content wants (read off the scroller, so the
      // body is never laid out unscrolled — that would reset where the
      // talent had scrolled to), then the CSS ceiling: the space above the
      // keyboard minus the tall sheet's top gap.
      const box = sheet.getBoundingClientRect().height;
      const natural = body ? box - body.clientHeight + body.scrollHeight : box;
      const cap = parseFloat(getComputedStyle(sheet).maxHeight) || avail;
      const keepTop = avail - top;
      const height =
        natural <= cap ? natural : Math.min(cap, Math.max(keepTop, Math.min(MIN_SHEET, cap)));
      sheet.style.height = `${height}px`;
    });
    if (body && body.scrollTop !== scrolled) body.scrollTop = scrolled;
  });
}

/** The keyboard has gone and the webview is full height again: every sheet
    back to its own size. Run only once the webview has grown back, so the
    before and after are measured against the same screen; a sheet that rode
    up with the keyboard glides back down, a tall one does not move at all. */
export function releaseSheets(): void {
  document.querySelectorAll<HTMLElement>(".bsheet-overlay--kb").forEach((overlay) => {
    const sheet = sheetOf(overlay);
    if (!sheet) return;
    glide(sheet, () => {
      overlay.classList.remove("bsheet-overlay--kb");
      sheet.style.height = "";
      delete sheet.dataset.kbTop;
    });
  });
  document.documentElement.style.removeProperty("--kb-avail");
}
